import { sleep } from "@/lib/utils";

import type { Api, ReturnRequest, ReturnsSummary } from "../types";

// Offline-заявки на возврат для админ-консоли: заявки разных стадий и
// адресатов (один магазин и мультимагазинный заказ без shopId), включая
// просроченную — чтобы список, фильтры и действия было на чём проверить.
// Форма в точности повторяет ReturnRequest из api-gateway (services/returns.ts).
const DAY = 86_400_000;
const at = (offsetDays: number) => new Date(Date.now() + offsetDays * DAY).toISOString();

let store: ReturnRequest[] = [
	{
		id: "ort_9a1b2c3d",
		orderId: "ord_8f2a1c4e9b6d0a3f",
		orderNumber: 100245,
		shopId: "shop-silver-moon",
		shopName: "Silver Moon",
		buyerUserId: "usr-maria",
		buyerName: "Мария Соколова",
		kind: "return",
		reason: "Серьги пришли с царапиной на застёжке, хочу вернуть.",
		status: "requested",
		dueAt: at(2),
		overdue: false,
		daysLeft: 2,
		refundGoods: 3500,
		refundDelivery: 700,
		refundTotal: 4200,
		createdAt: at(-1),
		updatedAt: at(-1),
	},
	{
		id: "ort_4e5f6a7b",
		orderId: "ord_5a1f8c2b4e6d7090",
		orderNumber: 100247,
		shopId: "shop-natur",
		shopName: "НатурТекстиль",
		buyerUserId: "usr-olga",
		buyerName: "Ольга Иванова",
		kind: "dispute",
		reason: "Размер не соответствует описанию — заказывала M, пришёл S.",
		status: "requested",
		dueAt: at(-1),
		overdue: true,
		daysLeft: -1,
		refundGoods: 7200,
		refundDelivery: 0,
		refundTotal: 7200,
		createdAt: at(-6),
		updatedAt: at(-6),
	},
	{
		id: "ort_1c2d3e4f",
		orderId: "ord_3d7b9e0f1a2c5d8e",
		orderNumber: 100246,
		shopName: "Керамика Плюс · Уютный дом",
		buyerUserId: "usr-alexey",
		buyerName: "Алексей Кузнецов",
		kind: "return",
		reason: "Ваза разбилась при доставке, спор по мультимагазинному заказу.",
		status: "approved",
		dueAt: at(-3),
		overdue: false,
		daysLeft: -3,
		justified: true,
		resolvedByName: "Администратор",
		resolvedAt: at(-2),
		resolutionNote: "Обоснованный возврат: повреждение при доставке.",
		refundGoods: 9600,
		refundDelivery: 300,
		refundTotal: 9900,
		createdAt: at(-5),
		updatedAt: at(-2),
	},
	{
		id: "ort_7b8c9d0e",
		orderId: "ord_3d7b9e0f1a2c5d8e",
		orderNumber: 100246,
		shopId: "shop-uyut",
		shopName: "Уютный дом",
		buyerName: "Алексей Кузнецов",
		kind: "return",
		reason: "Плед не подошёл по цвету.",
		status: "received",
		dueAt: at(-8),
		overdue: false,
		daysLeft: -8,
		justified: false,
		resolvedByName: "Администратор",
		resolvedAt: at(-7),
		refundGoods: 12500,
		refundDelivery: 0,
		refundTotal: 12500,
		goodsReceivedAt: at(-1),
		createdAt: at(-10),
		updatedAt: at(-1),
	},
	{
		id: "ort_2f3a4b5c",
		orderId: "ord_old_0001",
		shopId: "shop-keramika",
		shopName: "Керамика Плюс",
		buyerName: "Ирина Петрова",
		kind: "return",
		reason: "Товар не соответствует фото.",
		reasonCode: "other",
		status: "rejected",
		dueAt: at(-12),
		overdue: false,
		daysLeft: -12,
		justified: false,
		resolvedByName: "Администратор",
		resolvedAt: at(-11),
		resolutionNote: "Товар соответствует описанию, следов брака нет.",
		refundGoods: 0,
		refundDelivery: 0,
		refundTotal: 0,
		createdAt: at(-14),
		updatedAt: at(-11),
	},
	{
		id: "ort_5d6e7f80",
		orderId: "ord_old_0002",
		shopId: "shop-silver-moon",
		shopName: "Silver Moon",
		buyerName: "Дмитрий Волков",
		kind: "return",
		reason: "Передумал, возврат в течение срока.",
		status: "refunded",
		dueAt: at(-20),
		overdue: false,
		daysLeft: -20,
		justified: true,
		resolvedByName: "Администратор",
		resolvedAt: at(-18),
		refundGoods: 4300,
		refundDelivery: 500,
		refundTotal: 4800,
		goodsReceivedAt: at(-16),
		refundedAt: at(-15),
		createdAt: at(-22),
		updatedAt: at(-15),
	},
];

const isOpenDispute = (r: ReturnRequest) => r.kind === "dispute" || r.dispute?.status === "open";

const summarise = (): ReturnsSummary => {
	const counts = new Map<string, number>();
	for (const r of store) counts.set(r.status, (counts.get(r.status) ?? 0) + 1);
	return {
		statuses: [...counts].map(([status, count]) => ({ status, count })),
		overdue: store.filter((r) => r.status === "requested" && r.overdue).length,
		total: store.length,
		openDisputes: store.filter(isOpenDispute).length,
	};
};

const find = (id: string): ReturnRequest => {
	const found = store.find((r) => r.id === id);
	if (!found) throw new Error(`Return request '${id}' not found`);
	return found;
};

const patch = (id: string, next: Partial<ReturnRequest>): ReturnRequest => {
	store = store.map((r) => (r.id === id ? { ...r, ...next, updatedAt: at(0) } : r));
	return find(id);
};

export const returns: Api["returns"] = {
	async list(status) {
		await sleep(160);
		const rows = status ? store.filter((r) => r.status === status) : store;
		return [...rows].sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));
	},
	async page(filter) {
		await sleep(160);
		const q = filter.search?.trim().toLowerCase();
		const rows = store
			.filter((r) => (!filter.status || r.status === filter.status) &&
				(!filter.openDisputes || isOpenDispute(r)) &&
				(!q || [r.orderId, r.buyerName, r.shopName].join(" ").toLowerCase().includes(q)))
			.sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));
		const start = (filter.page - 1) * filter.pageSize;
		return {
			items: rows.slice(start, start + filter.pageSize),
			total: rows.length,
			page: filter.page,
			pageSize: filter.pageSize,
			totalPages: Math.ceil(rows.length / filter.pageSize),
		};
	},
	async summary() {
		await sleep(120);
		return summarise();
	},
	async resolve(id, input) {
		await sleep(200);
		if (input.approve) {
			const row = find(id);
			return patch(id, {
				status: "approved",
				justified: Boolean(input.justified),
				resolutionNote: input.note,
				resolvedByName: "Администратор",
				resolvedAt: at(0),
				refundDelivery: input.justified ? row.refundDelivery : 0,
				refundTotal: row.refundGoods + (input.justified ? row.refundDelivery : 0),
				overdue: false,
			});
		}
		return patch(id, {
			status: "rejected",
			justified: undefined,
			resolutionNote: input.note,
			resolvedByName: "Администратор",
			resolvedAt: at(0),
			refundGoods: 0,
			refundDelivery: 0,
			refundTotal: 0,
			overdue: false,
		});
	},
	async received(id) {
		await sleep(200);
		return patch(id, { status: "received", goodsReceivedAt: at(0) });
	},
	async refunded(id) {
		await sleep(200);
		return patch(id, { status: "refunded", refundedAt: at(0) });
	},
	async get(id) {
		await sleep(120);
		const found = store.find((r) => r.id === id);
		if (!found) throw new Error("Return not found");
		return { ...found, lines: [], attachments: [], dispute: undefined };
	},
	async resolveDispute(id, input) {
		await sleep(150);
		const found = store.find((r) => r.id === id);
		if (!found) throw new Error("Return not found");
		return patch(id, {
			dispute: found.dispute ? { ...found.dispute, status: "closed", outcome: input.outcome } : undefined,
			resolutionNote: input.note ?? found.resolutionNote,
		});
	},
};
